import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useOrganizationStore } from '../store/organizationStore';
import { useAuthStore } from '../store/authStore';
import { getBookUserId, isAssistantUser } from '../lib/actingFor';
import api from '../lib/api';
import {
  OrganizationHeader,
  OrganizationInfo,
  OrganizationCRMFields,
  CoachesContactsSection,
  OrgActivityFeed,
  SportsTeamsSection,
  CompetitorFundraisers,
  OrgSnapshot,
  BlastFundraisers,
  MunchFundFundraisers,
} from '../components/organizations';
import { AssignmentInfoBanner } from '../components/shared/AssignmentInfoBanner';
import { AddPersonModal } from '../components/people';
import { ScheduleActivityModal } from '../components/activities';
import { ConvertToBlastModal, type ConvertInitialTeamContext } from '../components/people/ConvertToBlastModal';
import { TagChips, TagPicker } from '../components/tags';
import { useTagStore } from '../store/tagStore';
import type { PersonDetail } from '../types';

/**
 * Organization detail page
 * Layout order per spec:
 * 1. OrganizationHeader — name, badges, website
 * 2. SportsTeamsSection — primary content: sport → team → people hierarchy
 * 3. CoachesContactsSection — roll-up of all people (org/sport/team) with filter + sport dropdown
 * 4. OrganizationCRMFields — editable conference, division, Blast School ID
 * 5. OrganizationInfo — NCES reference data (bottom)
 */
export const OrganizationDetail = () => {
  const { id } = useParams<{ id: string }>();
  const {
    currentOrganization,
    isLoadingDetail,
    detailError,
    fetchOrganization,
    clearCurrentOrganization,
  } = useOrganizationStore();
  const { user: currentUser, isAdmin } = useAuthStore();
  const canDelete = isAdmin();

  // Tags (Org & Person Tags v1) — pills + add-control in the header.
  const { byRecord, loadForRecord, remove } = useTagStore();
  useEffect(() => {
    if (currentOrganization?.id) loadForRecord(currentOrganization.type, currentOrganization.id).catch(() => {});
  }, [currentOrganization?.id, currentOrganization?.type, loadForRecord]);
  const orgTags = currentOrganization ? (byRecord[`${currentOrganization.type}:${currentOrganization.id}`] ?? []) : [];

  // Add Person modal state
  const [isAddPersonOpen, setIsAddPersonOpen] = useState(false);
  const [addPersonSportId, setAddPersonSportId] = useState<string | null>(null);
  const [addPersonTeamId, setAddPersonTeamId] = useState<string | null>(null);

  // Schedule Activity modal state
  const [schedulePersonId, setSchedulePersonId] = useState<string | null>(null);
  const [schedulePersonName, setSchedulePersonName] = useState('');

  const handleScheduleActivity = (personId: string, personName: string) => {
    setSchedulePersonId(personId);
    setSchedulePersonName(personName);
  };

  // Convert to Blast modal state
  const [isConvertOpen, setIsConvertOpen] = useState(false);
  const [convertPerson, setConvertPerson] = useState<PersonDetail | null>(null);
  const [convertContext, setConvertContext] = useState<ConvertInitialTeamContext | null>(null);

  // CRM-33: the org detail always loads current + former (?includeFormer=true).
  // Revealing former coaches/teams is per-team/per-sport client-side state in
  // TeamRow/SportCard — no refetch, so the page never reloads on reveal.

  const handleConvert = useCallback(
    async (personId: string, teamId: string, sportId: string) => {
      if (!currentOrganization) return;

      const sport = currentOrganization.sports.find((s) => s.id === sportId);
      const team = sport?.teams.find((t) => t.id === teamId);
      if (!sport || !team) return;

      const ctx: ConvertInitialTeamContext = {
        organizationId: currentOrganization.id,
        organizationName: currentOrganization.name,
        organizationCity: currentOrganization.city || '',
        organizationState: currentOrganization.state || '',
        organizationAddress: currentOrganization.address || '',
        organizationZip: currentOrganization.zip || '',
        blastSchoolId: currentOrganization.blastSchoolId || null,
        sportName: sport.name,
        sportGender: sport.gender,
        teamLevel: team.level,
        blastTeamId: team.blastTeamId || null,
      };

      // Fetch the full person record — LinkedPerson in the org tree is a
      // slimmer variant and the modal expects PersonDetail (with
      // person.organizations[]) for its primary-org fallback path.
      try {
        const response = await api.get(`/api/people/${personId}`);
        const fullPerson = response.data.person as PersonDetail;
        setConvertPerson(fullPerson);
        setConvertContext(ctx);
        setIsConvertOpen(true);
      } catch {
        // Silent fail — no toast (matches existing org-page error patterns)
      }
    },
    [currentOrganization],
  );

  const handleConvertClose = useCallback(() => {
    setIsConvertOpen(false);
    setConvertPerson(null);
    setConvertContext(null);
  }, []);

  const handleConvertSuccess = useCallback(() => {
    handleConvertClose();
    if (id) fetchOrganization(id, { includeFormer: true });
  }, [id, fetchOrganization, handleConvertClose]);

  const handleAddPerson = useCallback((sportId: string | null, teamId: string | null) => {
    setAddPersonSportId(sportId);
    setAddPersonTeamId(teamId);
    setIsAddPersonOpen(true);
  }, []);

  const handleAddPersonSuccess = useCallback(() => {
    if (id) {
      fetchOrganization(id, { includeFormer: true });
    }
  }, [id, fetchOrganization]);

  // Fetch the org once per id (always with current + former data).
  useEffect(() => {
    if (id) fetchOrganization(id, { includeFormer: true });
  }, [id, fetchOrganization]);

  // Record the view once per org (NOT on every toggle) and clear on unmount.
  useEffect(() => {
    if (id) {
      // Record view (fire-and-forget)
      api.post('/api/views', { organizationId: id }).catch(() => {});
    }

    return () => {
      clearCurrentOrganization();
    };
  }, [id, clearCurrentOrganization]);

  if (isLoadingDetail) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blast-600 mx-auto"></div>
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading organization...</p>
        </div>
      </div>
    );
  }

  if (detailError) {
    return (
      <div className="card p-8 text-center">
        <p className="text-red-600 dark:text-red-400 font-medium">Error loading organization</p>
        <p className="text-gray-500 dark:text-gray-400 mt-1">{detailError}</p>
      </div>
    );
  }

  if (!currentOrganization) {
    return (
      <div className="card p-8 text-center">
        <p className="text-gray-500 dark:text-gray-400">Organization not found</p>
      </div>
    );
  }

  // Ownership predicate — aligned with the org-list "Mine" filter chip and the
  // backend assignedRep projection (active NamedAccount owner). Unassigned orgs
  // (no active NamedAccount) are OPEN to any rep — unclaimed-open rule,
  // mirrored by canEditPerson on the backend for the people a rep creates here.
  // Orgs assigned to another rep stay read-only for non-owners (UX cue, not a
  // security boundary — the org PATCH endpoint has no per-org role gate today).
  const assignedRep = currentOrganization.assignedRep ?? null;
  const isOwner = !!assignedRep && assignedRep.id === getBookUserId(currentUser);
  const isManagerial =
    currentUser?.role === 'MANAGER' || currentUser?.role === 'ADMIN';
  const canEdit = isOwner || !assignedRep || isManagerial;
  // Convert creates Blast accounts under the signed-in user, so assistants never get it.
  const canConvert = canEdit && !isAssistantUser(currentUser);
  // Banner stays informational for reps without a NamedAccount claim — shown
  // even on unassigned orgs they can now edit. Managers/admins never see it.
  const bannerVariant: 'assigned-to-other' | 'unassigned' | null =
    isOwner || isManagerial ? null : assignedRep ? 'assigned-to-other' : 'unassigned';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {bannerVariant && (
          <AssignmentInfoBanner
            variant={bannerVariant}
            assigneeName={assignedRep?.displayName ?? undefined}
          />
        )}

        {/* 1. Header */}
        <OrganizationHeader
          organization={currentOrganization}
          onAddPerson={canEdit ? () => handleAddPerson(null, null) : undefined}
          canEdit={canEdit}
        />

        {/* Tags — pill row + add-control (backend enforces apply permission) */}
        {currentOrganization && (
          <div className="mt-2 space-y-2">
            <TagChips tags={orgTags} onRemove={(t) => remove(currentOrganization.type, currentOrganization.id, t)} />
            <div className="max-w-xs"><TagPicker kind={currentOrganization.type} entityId={currentOrganization.id} /></div>
          </div>
        )}

        <OrgSnapshot organization={currentOrganization} />

        {/* 2. Sports & Teams — primary content area */}
        <SportsTeamsSection
          organization={currentOrganization}
          onLinkPerson={canEdit ? handleAddPerson : undefined}
          onScheduleActivity={canEdit ? handleScheduleActivity : undefined}
          onConvert={canConvert ? handleConvert : undefined}
          canEdit={canEdit}
          canDelete={canDelete}
        />

        {/* 3. Coaches & Contacts — roll-up of org/sport/team people, filterable */}
        <CoachesContactsSection
          organization={currentOrganization}
          onAddPerson={canEdit ? handleAddPerson : undefined}
          onScheduleActivity={handleScheduleActivity}
          onConvert={canConvert ? handleConvert : undefined}
          canEdit={canEdit}
        />

        {/* Activity — org-level roll-up (CRM-26) */}
        <OrgActivityFeed organizationId={currentOrganization.id} />

        <BlastFundraisers organization={currentOrganization} />

        <MunchFundFundraisers campaigns={currentOrganization.munchFundCampaigns ?? []} />

        {/* 3.5 Competitor Fundraising */}
        <CompetitorFundraisers organizationId={currentOrganization.id} canEdit={canEdit} />

        {/* 4. CRM Fields (editable) */}
        <OrganizationCRMFields organization={currentOrganization} canEdit={canEdit} />

        {/* 5. School Information (NCES data — bottom) */}
        <OrganizationInfo organization={currentOrganization} />
      </div>

      {/* Schedule Activity — open to anyone (org page has no nudge data for
          target persons; nudge is rendered on the Person Detail page) */}
      {schedulePersonId && (
        <ScheduleActivityModal
          isOpen={!!schedulePersonId}
          onClose={() => setSchedulePersonId(null)}
          personId={schedulePersonId}
          personName={schedulePersonName}
          onScheduled={() => setSchedulePersonId(null)}
        />
      )}

      {/* Record-edit modals — owner / manager / admin only */}
      {canEdit && (
        <>
          <AddPersonModal
            isOpen={isAddPersonOpen}
            onClose={() => setIsAddPersonOpen(false)}
            organizationId={currentOrganization.id}
            organizationName={currentOrganization.name}
            sports={currentOrganization.sports}
            preSelectedSportId={addPersonSportId}
            preSelectedTeamId={addPersonTeamId}
            onSuccess={handleAddPersonSuccess}
          />

          {convertPerson && convertContext && (
            <ConvertToBlastModal
              isOpen={isConvertOpen}
              onClose={handleConvertClose}
              person={convertPerson}
              onSuccess={handleConvertSuccess}
              initialTeamContext={convertContext}
            />
          )}
        </>
      )}
    </div>
  );
};
